import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, ArrowLeft, DollarSign } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { cashoutAPI } from '../services/api';

const Cashout = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const username = localStorage.getItem('username') || 'User';
  const [showDialog, setShowDialog] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('Stock Purchase');
  const [note, setNote] = useState('');
  const [cashoutHistory, setCashoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCashouts = useCallback(async () => {
    try {
      setLoading(true);
      const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).split('/').join('/');
      const data = await cashoutAPI.getAll(today);
      setCashoutHistory(data);
    } catch (error) {
      console.error('Failed to load cashouts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    loadCashouts();
  }, [navigate, loadCashouts]);

  const handleLogout = () => {
    navigate('/dashboard?action=logout');
  };

  const handleProcessCashout = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid amount",
        variant: "destructive"
      });
      return;
    }

    try {
      const cashoutRecord = await cashoutAPI.create({
        amount: parseFloat(amount),
        reason: note ? `${reason} - ${note}` : reason
      });

      setCashoutHistory([cashoutRecord, ...cashoutHistory]);

      toast({
        title: "Cashout Processed",
        description: `₹${amount} recorded for ${reason}`
      });

      // Reset form
      setAmount('');
      setReason('Stock Purchase');
      setNote('');
      setShowDialog(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to process cashout",
        variant: "destructive"
      });
    }
  };

  const getTodayTotal = () => {
    return cashoutHistory.reduce((sum, record) => sum + record.amount, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#e8ecf0' }}>
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#e8ecf0' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_6a116685-1875-4e0c-97f5-a9213e7bf53a/artifacts/isz5lodv_IMG_6110.PNG" 
              alt="New Shop Logo" 
              className="h-10 object-contain"
            />
            <span className="text-xl font-semibold text-gray-800">Cashout Management</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">Welcome, <span className="font-semibold">{username}</span></span>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Today's Summary */}
        <Card className="bg-white mb-6">
          <CardHeader>
            <CardTitle className="text-xl">Today's Cashout Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-sm text-gray-600">Total Cashout Today</p>
              <p className="text-4xl font-bold text-gray-900">₹{getTodayTotal().toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>

        {/* New Cashout Button */}
        <Button
          onClick={() => setShowDialog(true)}
          className="w-full mb-6 h-16 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-lg"
          data-testid="new-cashout-btn"
        >
          <DollarSign className="w-6 h-6 mr-2" />
          New Cashout
        </Button>

        {/* Cashout History */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-xl">Cashout History</CardTitle>
          </CardHeader>
          <CardContent>
            {cashoutHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No cashout records yet</p>
            ) : (
              <div className="space-y-3">
                {cashoutHistory.map((record) => (
                  <div key={record.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-lg">₹{record.amount.toFixed(2)}</p>
                        <p className="text-sm text-gray-600">{record.reason}</p>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <p>{record.date}</p>
                        <p>{record.created_at ? new Date(record.created_at).toLocaleTimeString('en-IN', { hour12: false }) : ''}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Processed by: {record.username}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cashout Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">New Cashout</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="amount">Amount (₹) *</Label>
              <Input
                id="amount"
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="0.01"
                data-testid="cashout-amount"
              />
            </div>

            <div>
              <Label htmlFor="reason">Reason *</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger id="reason" data-testid="cashout-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Stock Purchase">Stock Purchase</SelectItem>
                  <SelectItem value="Stock Delivery">Stock Delivery</SelectItem>
                  <SelectItem value="Cash Pickup">Cash Pickup</SelectItem>
                  <SelectItem value="Store Maintenance">Store Maintenance</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="note">Additional Note</Label>
              <Textarea
                id="note"
                placeholder="Add any additional notes (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                data-testid="cashout-note"
              />
            </div>
          </div>
          <DialogFooter>
            <div className="flex space-x-2 w-full">
              <Button
                onClick={handleProcessCashout}
                className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold"
                data-testid="process-cashout-btn"
              >
                Process Cashout
              </Button>
              <Button
                onClick={() => setShowDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cashout;
